// MedicalHistoryModal.js
import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Nav } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { createUserMedicalHistory } from 'apiServices/reducer/dialogApiService';
import '../../../styles/user.css';
import CustomSuccessModal from '../../../components/CustomSuccessModal'; 

const MedicalHistoryModal = ({ show, handleClose, rowData, refreshMedicalHistory, userId }) => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const [activeTab, setActiveTab] = useState('medical');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState(''); 
    const [fieldErrors, setFieldErrors] = useState({ medical_history: false, surgical_history: false, dental_history: false });
    const inputErrorStyle = { border: '1px solid #dc3545', boxShadow: 'none' };
    const dispatch = useDispatch();

    const token = useSelector((state) => state.apiService?.token);

    useEffect(() => {
        if (rowData) {
            reset({
                medical_history: rowData.medical_history || '',
                surgical_history: rowData.surgical_history || '',
                dental_history: rowData.dental_history || ''
            });
        } else {
            reset({
                medical_history: '',
                surgical_history: '',
                dental_history: ''
            });
        }
        setFieldErrors({ medical_history: false, surgical_history: false, dental_history: false });
    }, [rowData, reset, show]);

    const onInvalid = () => { 
    };

    const formatHistoryInput = (input) => {
        if (!input) return '';
        // Remove any existing bullet points or numbers from pasted text
        return input.replace(/^[•\-\d\.\s]+/gm, '')
                   .split('\n')
                   .filter(line => line.trim() !== '')
                   .map(line => line.trim())
                   .join('\n');
    };

    const onSave = async (data) => {
        // Check if at least one field has data
        const hasData = data.medical_history?.trim() || data.surgical_history?.trim() || data.dental_history?.trim();
        
        if (!hasData) {
            setFieldErrors({ medical_history: true, surgical_history: true, dental_history: true });
            return;
        }

        setFieldErrors({ medical_history: false, surgical_history: false, dental_history: false });
        const formBody = {
            medical_history: formatHistoryInput(data.medical_history || ''),
            surgical_history: formatHistoryInput(data.surgical_history || ''),
            dental_history: formatHistoryInput(data.dental_history || ''),
            user_id: userId,
            token,
        };

        try {
            const response = await dispatch(createUserMedicalHistory(formBody)).unwrap();
            setSuccessMessage(response.OutputMessage || 'Medical history saved successfully!');
            setShowSuccessModal(true);
            reset();
            handleClose();
            refreshMedicalHistory();
        } catch (error) {
            console.error('API Error:', error); 
        }
    };

    return (
        <>
            <Modal show={show} onHide={handleClose} dialogClassName="medical-history-modal" backdrop="static" keyboard={false} onMouseDown={(e) => e.stopPropagation()}>
                <Modal.Header closeButton className='modal-header-bg'>
                    <Modal.Title className='text-capitalize'>{'Add Medical History' }</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit(onSave, onInvalid)} autoComplete="off">
                        <Nav variant="tabs" defaultActiveKey="#medical" className="mb-3 custom-tabs">
                            {['medical', 'surgical', 'dental'].map((tab) => (
                                <Nav.Item key={tab}>
                                    <Nav.Link
                                        active={activeTab === tab}
                                        onClick={() => setActiveTab(tab)}
                                    >
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)} History
                                    </Nav.Link>
                                </Nav.Item>
                            ))}
                        </Nav>

                        <div className="tab-content">
                            <div className={`tab-pane fade ${activeTab === 'medical' ? 'show active' : ''}`}>
                                <Form.Group controlId="medical_history">
                                    <Form.Control
                                        as="textarea"
                                        rows={15}
                                        columns={30}
                                        {...register('medical_history')}
                                        className={`form-control custom-textarea ${fieldErrors.medical_history ? 'error-border' : ''}`}
                                        style={fieldErrors.medical_history ? inputErrorStyle : {}}
                                        placeholder="Enter medical history (optional)"
                                        onChange={(e) => {
                                            if (e.target.value.trim()) {
                                                setFieldErrors((prev) => ({ ...prev, medical_history: false }));
                                            }
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    />
                                </Form.Group>
                            </div>
                            <div className={`tab-pane fade ${activeTab === 'surgical' ? 'show active' : ''}`}>
                                <Form.Group controlId="surgical_history">
                                    <Form.Control
                                        as="textarea"
                                        rows={15}
                                        columns={30}
                                        {...register('surgical_history')}
                                        placeholder={`Example:\nAppendectomy (2010)\nKnee Replacement (2015)`}
                                        className={`form-control custom-textarea ${fieldErrors.surgical_history ? 'error-border' : ''}`}
                                        style={fieldErrors.surgical_history ? inputErrorStyle : {}}
                                        onChange={(e) => {
                                            if (e.target.value.trim()) {
                                                setFieldErrors((prev) => ({ ...prev, surgical_history: false }));
                                            }
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    />
                                </Form.Group>
                            </div>
                            <div className={`tab-pane fade ${activeTab === 'dental' ? 'show active' : ''}`}>
                                <Form.Group controlId="dental_history">
                                    <Form.Control
                                        as="textarea"
                                        rows={15}
                                        columns={30}
                                        {...register('dental_history')}
                                        placeholder={`Example:\nRoot Canal (2018)\nWisdom Teeth Extraction (2020)`}
                                        className={`form-control custom-textarea ${fieldErrors.dental_history ? 'error-border' : ''}`}
                                        style={fieldErrors.dental_history ? inputErrorStyle : {}}
                                        onChange={(e) => {
                                            if (e.target.value.trim()) {
                                                setFieldErrors((prev) => ({ ...prev, dental_history: false }));
                                            }
                                        }}
                                        onMouseDown={(e) => e.stopPropagation()}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer className=" modal-header-bg justify-content-center">
                    <Button variant="primary" className="me-2 text-capitalize" onClick={handleSubmit(onSave, onInvalid)}>Save Changes</Button>
                    <Button variant="danger" className="ms-2 text-capitalize" onClick={handleClose}>Close</Button>
                </Modal.Footer>
            </Modal>
            <CustomSuccessModal
              show={showSuccessModal}
              onHide={() => { setShowSuccessModal(false); setSuccessMessage(''); }}
              message={successMessage}
            /> 
            <style>{`.error-border { border: 1px solid #dc3545 !important; box-shadow: none !important; }`}</style>
        </>
    );
};

export default MedicalHistoryModal;